// Danh sách biên bản thỏa thuận đã lập của hóa đơn, xem PDF từng biên bản
import MessageBox from "components/shared-components/MessageBox";
import ReactPdfViewer from "components/shared-components/ReactPdfViewer";
import { AGREEMENT_REPORT_TITLE } from "constants/AgreementReportConstant";
import {
  useDownloadAgreement,
  useGetAgreementList,
} from "hooks/invoiceAgreement/useInvoiceAgreement";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  Button,
  Col,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { formatAgreementNumber } from "pages/IssueInvoice/helper/agreementPending";

// Mỗi biên bản có nhiều file (bản nháp mỗi lần lưu + bản đã ký).
// Chỉ bản đã ký mới được hiển thị: file nào có sign_method khác null.
const getSignedFile = item =>
  (item?.files || []).find(file => file?.sign_method != null);

const AgreementListModal = props => {
  const { isOpen, toggle, invoiceId } = props;
  const { t, i18n } = useTranslation(["issueInvoice"]);

  const [items, setItems] = useState([]);
  const [agreementId, setAgreementId] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isError, setIsError] = useState(false);

  const selectedItem = items.find(item => item.agreement_id === agreementId);
  const selectedFile = getSignedFile(selectedItem);

  const { mutate: mutateDownloadAgreement, isLoading: isLoadingPdf } =
    useDownloadAgreement({
      onSuccess: response => {
        setPdfUrl(URL.createObjectURL(response.data));
      },
      onError: () => {
        setIsError(true);
      },
    });

  const showAgreementPdf = item => {
    setPdfUrl(null);
    setIsError(false);
    setAgreementId(item?.agreement_id ?? null);

    // Chưa có bản đã ký thì không gọi API tải file
    if (getSignedFile(item)) {
      mutateDownloadAgreement({ agreement_id: item.agreement_id });
    }
  };

  const { mutate: mutateGetAgreementList, isLoading: isLoadingList } =
    useGetAgreementList({
      onSuccess: response => {
        const listAgreement = response.data?.items || [];
        setItems(listAgreement);
        // Mặc định mở biên bản đầu tiên đã có bản ký
        showAgreementPdf(
          listAgreement.find(item => getSignedFile(item)) || listAgreement[0]
        );
      },
      onError: () => {
        setIsError(true);
      },
    });

  // Mở modal thì nạp danh sách, đóng modal thì xóa sạch
  useEffect(() => {
    if (isOpen && invoiceId) {
      setIsError(false);
      mutateGetAgreementList({ id: invoiceId });
    } else {
      setItems([]);
      setAgreementId(null);
      setPdfUrl(null);
      setIsError(false);
    }
  }, [isOpen]);

  // Thu hồi object URL cũ để không rò bộ nhớ
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const styleDiv = {
    width: "100%",
    height: "65vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#8080802b",
  };

  const getItemTitle = item =>
    AGREEMENT_REPORT_TITLE[item.type] || item.type || "";

  // Một loại lập được nhiều lần -> ghi rõ số biên bản + ngày lập để phân biệt
  const getItemLabel = item =>
    `${getItemTitle(item)} - ${formatAgreementNumber(item)}`;

  const agreementOptions = items
    .filter(item => item.sign_method)
    .map(item => ({
      value: item.agreement_id,
      label: `${getItemLabel(item)} ${
        item.sign_method
          ? `[${t(`form.sign_method_agreement.${item?.sign_method}`)}]`
          : ""
      }`,
    }));

  const renderPdfArea = () => {
    if (isLoadingList || isLoadingPdf) {
      return (
        <div style={styleDiv}>
          <Spinner color="primary" />
        </div>
      );
    }

    if (isError) {
      return (
        <div style={styleDiv}>
          <MessageBox
            variant="danger"
            classMore="d-sm-flex align-items-center justify-content-center gap-1 font-size-18"
          >
            <i className="bx bxs-error-alt"></i>Không tải được file biên bản
          </MessageBox>
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <div style={styleDiv}>
          <MessageBox
            variant="warning"
            classMore="d-sm-flex align-items-center justify-content-center gap-1 font-size-18"
          >
            <i className="bx bx-info-circle"></i>Hóa đơn chưa có biên bản thỏa
            thuận nào
          </MessageBox>
        </div>
      );
    }

    if (!selectedFile) {
      return (
        <div style={styleDiv}>
          <MessageBox
            variant="warning"
            classMore="d-sm-flex align-items-center justify-content-center gap-1 font-size-18"
          >
            <i className="bx bx-time-five"></i>Biên bản này chưa có file PDF
          </MessageBox>
        </div>
      );
    }

    return pdfUrl ? (
      <ReactPdfViewer filePdfUrl={pdfUrl} style={{ height: "65vh" }} />
    ) : (
      <div style={styleDiv}>
        <Spinner color="primary" />
      </div>
    );
  };

  return (
    <Modal
      size="xl"
      contentClassName="confirm-modal"
      isOpen={isOpen}
      role="dialog"
      autoFocus={true}
      centered={true}
      className="exampleModal"
      toggle={toggle}
    >
      <div className="modal-content">
        <ModalHeader toggle={toggle}>
          <Label>Biên bản thỏa thuận đã ký</Label>
        </ModalHeader>
        <ModalBody>
          {items.length > 0 && (
            <Row className="mb-3 align-items-center">
              <Select
                placeholder="Chọn loại biên bản..."
                value={
                  agreementOptions.find(
                    option => option.value === agreementId
                  ) || null
                }
                onChange={option => {
                  showAgreementPdf(
                    items.find(item => item.agreement_id === option?.value)
                  );
                }}
                options={agreementOptions}
                isSearchable={true}
                classNamePrefix="select2-selection"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                }}
              />
            </Row>
          )}
          {renderPdfArea()}
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            color="danger"
            className="me-2"
            onClick={toggle}
          >
            Đóng
          </Button>
          {pdfUrl && (
            <Button
              type="button"
              color="success"
              className="me-2 btn-label"
              onClick={() => {
                const link = document.createElement("a");
                link.href = pdfUrl;
                link.setAttribute(
                  "download",
                  selectedItem
                    ? `${getItemLabel(selectedItem)}.pdf`
                    : "bien-ban.pdf"
                );
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              <i className="bx bx-download label-icon" />
              Tải PDF
            </Button>
          )}
        </ModalFooter>
      </div>
    </Modal>
  );
};

AgreementListModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  invoiceId: PropTypes.any,
};

export default AgreementListModal;
